import React, { useState } from "react";
import { StringHelper } from "@bryntum/schedulerpro";
import { Button } from "@/modern/components/ui/button";
import { Checkbox } from "@/modern/components/ui/checkbox";
import { Label } from "@/modern/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/modern/components/ui/dropdown-menu";
import { exportDataApi, exportMarketingTagsApi } from "@/network/network";
import { serverErrorMsg, showToast, downloadFileFromData } from "@/other/utils/utils";
import { IMPORT_TYPES } from "@/other/constants/OtherKeys";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { shouldShowAsyncDialog } from "@/bryntum-lib/editors/AsyncTaskMessageEditor/SwivuAsyncMessagePopup";
import { ChevronDown } from "lucide-react";

interface DownloadTabProps {
  record: {
    importType: string;
    importHeader: string;
  };
  context: any;
  isEntityArchive: boolean;
}

export const DownloadTab: React.FC<DownloadTabProps> = ({ record, context, isEntityArchive }) => {
  const [includeArchived, setIncludeArchived] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleDownload = async (exportType?: "template" | "complete") => {
    const { importType } = record;
    const selectedBoardSlug = context.selectedBoard.record.slug;

    if (importType === IMPORT_TYPES.marketingTags) {
      setIsExporting(true);
      try {
        const response = await exportMarketingTagsApi(selectedBoardSlug);
        downloadFileFromData(response.data, `${selectedBoardSlug}-marketing-tags-export.csv`);
      } catch (error: any) {
        if (error.response) showToast({ msg: serverErrorMsg(), type: "error", duration: 3000 });
      } finally {
        setIsExporting(false);
      }
      return;
    }

    let updatedImportType = importType;
    let params: any = {};

    if (updatedImportType === IMPORT_TYPES.boardUsers) {
      updatedImportType = "Board Users";
    } else if (exportType) {
      params = { export_type: exportType };
    }

    if (isEntityArchive) {
      params = { ...params, include_archived: includeArchived };
    }

    const showDialog = shouldShowAsyncDialog("CSV_EXPORT");
    showDialog &&
      context.editorHandler.openEditor(
        EDITOR_KEYS.asyncTaskMessageEditor,
        null,
        { record: "CSV_EXPORT" },
        { useExisting: true, by: "recordType" }
      );

    setIsExporting(true);
    try {
      await exportDataApi(selectedBoardSlug, importType, params);
    } catch (error: any) {
      if (error.response) showToast({ msg: serverErrorMsg(), type: "error", duration: 3000 });
    } finally {
      setIsExporting(false);
    }
  };

  const getDisplayImportType = () => {
    const updatedImportType = record.importType;
    if (updatedImportType === IMPORT_TYPES.boardUsers) {
      return "Board Users";
    }
    return StringHelper.capitalize(updatedImportType);
  };

  const isBudgetType = record.importType === IMPORT_TYPES.taskBudget;

  return (
    <div className="flex flex-col items-center justify-center p-8 gap-6 h-full">
      {isEntityArchive && (
        <div className="flex items-center space-x-2">
          <Checkbox id="include-archived" checked={includeArchived} onCheckedChange={(checked) => setIncludeArchived(checked === true)} />
          <Label htmlFor="include-archived" className="text-sm font-medium cursor-pointer">
            Include Archived Records
          </Label>
        </div>
      )}

      {isBudgetType ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="lg" disabled={isExporting} className="min-w-[250px]">
              {isExporting ? "Exporting..." : `Export ${getDisplayImportType()} (CSV)`}
              <ChevronDown className="ml-2 h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => handleDownload("template")}>Export editable fields</DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleDownload("complete")}>Export all fields</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <Button onClick={() => handleDownload()} size="lg" disabled={isExporting} className="min-w-[250px]">
          {isExporting ? "Exporting..." : `Export ${getDisplayImportType()} (CSV)`}
        </Button>
      )}
    </div>
  );
};
